import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { User, Store, ArrowRight, ShoppingCart, FileText, ShieldCheck } from 'lucide-react';

/**
 * Unified registration hub — pick role first, then jump to the appropriate
 * dedicated registration form (customer = simple, vendor = multi-step KYC).
 */
const RegisterHub = () => {
  const [params] = useSearchParams();
  const recommended = params.get('as') === 'vendor' ? 'vendor' : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0b1220]" data-testid="register-hub">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">Join TelanganaMart</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Choose how you&apos;d like to register</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* CUSTOMER CARD */}
          <Link
            to="/customer/register"
            className={`group relative bg-white dark:bg-[#111827] border-2 ${recommended === null ? 'border-[#ee5e23]' : 'border-gray-200 dark:border-gray-800'} rounded-lg p-6 hover:border-[#ee5e23] hover:shadow-lg transition`}
            data-testid="register-card-customer"
          >
            {recommended === null && (
              <span className="absolute top-3 right-3 bg-[#ee5e23] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">Most popular</span>
            )}
            <div className="w-14 h-14 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mb-4">
              <User className="w-7 h-7 text-[#ee5e23]" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Register As Customer</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Buyers, procurement teams, and anyone sourcing from Telangana vendors.</p>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1.5 mb-5">
              <li className="flex items-start gap-2"><ShoppingCart className="w-4 h-4 text-[#ee5e23] mt-0.5 shrink-0" /> Browse 1000+ verified vendors</li>
              <li className="flex items-start gap-2"><FileText className="w-4 h-4 text-[#ee5e23] mt-0.5 shrink-0" /> Post requirements, get instant quotes</li>
              <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-[#ee5e23] mt-0.5 shrink-0" /> Save favorite suppliers</li>
            </ul>
            <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#ee5e23] group-hover:gap-2.5 transition-all">
              Continue as Customer <ArrowRight className="w-4 h-4" />
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
              Takes ~30 seconds · Mobile + OTP
            </div>
          </Link>

          {/* VENDOR CARD */}
          <Link
            to="/vendor/register"
            className={`group relative bg-white dark:bg-[#111827] border-2 ${recommended === 'vendor' ? 'border-[#2c54b2]' : 'border-gray-200 dark:border-gray-800'} rounded-lg p-6 hover:border-[#2c54b2] hover:shadow-lg transition`}
            data-testid="register-card-vendor"
          >
            {recommended === 'vendor' && (
              <span className="absolute top-3 right-3 bg-[#2c54b2] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">Recommended</span>
            )}
            <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
              <Store className="w-7 h-7 text-[#2c54b2]" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">Register As Vendor</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Manufacturers, wholesalers, distributors, and service providers in Telangana.</p>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1.5 mb-5">
              <li className="flex items-start gap-2"><Store className="w-4 h-4 text-[#2c54b2] mt-0.5 shrink-0" /> Free storefront, GST-verified badge</li>
              <li className="flex items-start gap-2"><FileText className="w-4 h-4 text-[#2c54b2] mt-0.5 shrink-0" /> Receive buyer requirements as leads</li>
              <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-[#2c54b2] mt-0.5 shrink-0" /> Upload certificates, build trust</li>
            </ul>
            <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2c54b2] group-hover:gap-2.5 transition-all">
              Continue as Vendor <ArrowRight className="w-4 h-4" />
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
              Takes ~3 minutes · Business details required
            </div>
          </Link>
        </div>

        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-8">
          Already have an account? <Link to="/login" className="font-semibold text-[#ee5e23] hover:underline" data-testid="register-hub-signin-link">Sign in here</Link>
        </p>
      </div>
      <Footer />
    </div>
  );
};

export default RegisterHub;
